import {
  AddonScope,
  AddonsRequest,
  ProductParametersDto,
  productParametersFactory,
  QuoteParametersDto,
  quoteParametersFactory,
  RequestDetailsDto,
  SignupDto,
} from '@sportngin/se-commerce-sdk';
import {
  FactoryFn,
  Persona,
  personaToParticipantDto,
  personaToRegistrantDto,
  ProductReference,
  Reference,
} from '../../data/model';

export type ComponentRenderMode =
  'shop' | 'checkout' | 'hidden' | 'user' | undefined;

export interface Signup {
  program?: Program; // Ngin-specific; make it optional for now
  registration?: Registration; // Ngin-specific; make it optional for now
  participant: Persona;
  requestDetails: RequestDetails;
}

export interface HostApplication {
  baseUriProxy: string; // e.g. https://www.mysite.com
}

export interface ContextData {
  bossOrganization: BossOrganization;
  guestKey: string;
  enabledSalesChannels: string[];
  registrant: Persona;
  signups: Signup[];
  multipleRegistrations: MultipleRegistrations;
  analyticsPageAttributes: AnalyticsPageAttributes;
  seApiToken?: string; // token for SE API access
  hostApplication?: HostApplication;
}

export interface Program {
  id: number;
  system: string; // will always be 'Ngin'
  type: string; // will always be 'Survey'
  name: string;
}

export interface Registration {
  id: number;
  system: string; // will always be 'Ngin'
  type: string; // will always be 'SurveyResult'
}

export interface BossOrganization {
  id: number;
  name: string;
  addrState: string;
  sports: string;
  orgLogo: string;
}

export interface MultipleRegistrations {
  enabled: boolean;
  buttonLabel: string | null;
  registrations?: Registration[]; // if single registration, might not be present
}

export interface RequestDetails {
  eventCost: number | null;
  customerPaysFee?: boolean;
  eventType: string | null;
  paymentDate: Date | null;
  programEndDate: Date | null;
  programStartDate: Date | null;
  sport: string | null;
  submittedBy: string | null;
  orgName?: string;
  eventName?: string;
  recommended?: boolean;
}

export interface AnalyticsPageAttributes {
  depth1: string;
  depth2: string;
  depth3: string;
  depth4?: string;
  surveySport: string;
  surveyType: string;
  surveyRegistrantType: string;
  surveyParticipantType: string;
  surveyParticipantId: number;
  surveyParticipantDob: Date;
  surveyParticipantGender: string;
  bossOrganizationId: BossOrganization['id'];
  bossOrganizationName: BossOrganization['name'];
  bossOrganizationAddrState: BossOrganization['addrState'];
  bossOrganizationSports: BossOrganization['sports'];
  bossOrganizationLogo: BossOrganization['orgLogo'];
}

export interface ProductQuery {
  enabledSalesChannels: string[];
  signupType: string;
  registrant: Persona;
  signups: Signup[];
  organizationId?: number;
  guestKey?: string;
}

export interface QuoteQuery {
  provider: string | undefined;
  registrant: Persona;
  participant: Persona;
  requestDetails: RequestDetails;
  signupType: string;
}

export const quoteQueryFactory = (
  contextData: ContextData,
  quoteProvider: string | undefined,
  recommended?: boolean,
): QuoteQuery => {
  const signup = contextData.signups[0];
  const query: QuoteQuery = {
    registrant: contextData.registrant,
    participant: signup.participant,
    requestDetails: {
      ...signup.requestDetails,
      orgName: contextData.bossOrganization.name,
      eventName: signup.program?.name,
      recommended: recommended,
    },
    provider: quoteProvider,
    signupType: contextData.analyticsPageAttributes.surveyType,
  };
  return query;
};

export const contextDataToProductReference: FactoryFn<
  ContextData,
  ProductReference
> = (src: ContextData) => {
  const result = new ProductReference();
  const signup = src.signups[0];
  if (signup.program && signup.registration && signup.participant) {
    result.program = new Reference({
      id: signup.program.id,
      name: signup.program.name,
      type: signup.program.type,
      system: signup.program.system,
    });
    result.registration = new Reference({
      id: signup.registration.id,
      type: signup.registration.type,
      system: signup.registration.system,
    });
    result.participant = new Reference({
      id: signup.participant.id || 0,
      name: signup.participant.firstName + ' ' + signup.participant.lastName,
      type: signup.participant.type || '',
      system: signup.participant.system || '',
      dob: signup.participant.dob || '',
      gender: signup.participant.gender || '',
    });
  }

  return result;
};

export const requestDetailsToRequestDetailsDto: FactoryFn<
  RequestDetails,
  RequestDetailsDto
> = (src: RequestDetails) => {
  return {
    eventType: src.eventType,
    eventCost: src.eventCost,
    customerPaysFee: src.customerPaysFee || false,
    programStartDate: src.programStartDate,
    programEndDate: src.programEndDate,
    paymentDate: src.paymentDate || null,
    sport: src.sport,
    submittedBy: src.submittedBy,
    orgName: src.orgName,
    eventName: src.eventName,
    recommended: src.recommended,
  };
};

export const productQueryToProductParametersDto: FactoryFn<
  ProductQuery,
  ProductParametersDto
> = (src: ProductQuery) =>
  productParametersFactory({
    enabledSalesChannels: [...src.enabledSalesChannels],
    registrant: personaToRegistrantDto(src.registrant),
    signupType: src.signupType,
    signups: src.signups.map(signupToSignupDto),
    organizationId: src.organizationId,
    guestKey: src.guestKey,
    requestDetails: src.signups[0]?.requestDetails,
  });

export const quoteQueryToQuoteParametersDto: FactoryFn<
  QuoteQuery,
  QuoteParametersDto
> = (src: QuoteQuery) =>
  quoteParametersFactory({
    provider: src.provider || '',
    participant: personaToParticipantDto(src.participant),
    registrant: personaToRegistrantDto(src.registrant),
    requestDetails: requestDetailsToRequestDetailsDto(src.requestDetails),
    signupType: src.signupType,
  });

export const contextDataToProductQuery: FactoryFn<ContextData, ProductQuery> = (
  source: ContextData,
) => {
  // Filter out helper-only sales channel so it is never sent to backend APIs
  const HELPER_CHANNEL = 'verticalinsure-registrationcancellation-recommend';
  const channels = (source.enabledSalesChannels || []).filter(
    ch => (ch?.toLowerCase?.() ?? ch) !== HELPER_CHANNEL,
  );
  return {
    enabledSalesChannels: channels,
    registrant: source.registrant,
    signupType: source.analyticsPageAttributes.surveyType,
    signups: source.signups,
    organizationId: source.bossOrganization.id,
    guestKey: source.guestKey,
  };
};

export const parseContextDataJson = (data: string): ContextData => {
  const obj = JSON.parse(data);
  preprocessLegacyContextData(obj);
  const result: ContextData = {
    ...obj,
    analyticsPageAttributes: {
      ...obj.analyticsPageAttributes,
      surveyType: obj.analyticsPageAttributes.survey_type,
    },
  };
  if (result?.registrant?.address?.address2 === null) {
    result.registrant.address.address2 = undefined;
  }
  result.signups.forEach(signup => {
    if (signup?.participant?.address?.address2 === null) {
      signup.participant.address.address2 = undefined;
    }
  });
  return result;
};

export const signupToSignupDto: FactoryFn<Signup, SignupDto> = (
  src: Signup,
): SignupDto => {
  return {
    participant: personaToParticipantDto(src.participant),
    requestDetails: requestDetailsToRequestDetailsDto(src.requestDetails),
    programName: src.program?.name || '',
  };
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Backwards compatibility for json context data without multiple signups
 *
 * This is to aid with the deployment of the new embeddable store without causing regressions with existing integrations
 * including motion and ngin registration.
 *
 * This additional step can be removed once we are sure that all integrations are updated to the new format.
 */
const preprocessLegacyContextData = (contextJson: any): void => {
  const legacyKeys = [
    'program',
    'registration',
    'participant',
    'requestDetails',
  ];
  if (!contextJson.signups && legacyKeys.every(key => contextJson[key])) {
    const props: any = {};
    legacyKeys.forEach(key => {
      props[key] = contextJson[key];
      delete contextJson[key];
    });
    contextJson.signups = [{...props} as Signup];
  }
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const addonScopesFromContext: FactoryFn<ContextData, AddonsRequest> = (
  context: ContextData,
): AddonsRequest => {
  const scopes: AddonScope[] = [];
  const signup = context.signups[0];
  scopes.push({
    registration: signup.registration,
    participant: personaToParticipantDto(signup.participant),
    registrant: personaToRegistrantDto(context.registrant),
    requestDetails: requestDetailsToRequestDetailsDto({
      ...signup.requestDetails,
      orgName: context.bossOrganization.name,
      eventName: signup.program?.name,
    }),
  });

  const otherRegistrations = context.multipleRegistrations?.registrations || [];
  for (const registration of otherRegistrations) {
    if (registration.id !== signup.registration?.id) {
      scopes.push({registration});
    }
  }
  return {validScopes: scopes};
};
